import React, { DOMAttributes, ForwardedRef, HTMLAttributes } from 'react';
import clsx from 'clsx';
import { FocusRing } from 'react-aria/FocusRing';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';
import { useHover } from 'react-aria/useHover';
import { mergeProps } from 'react-aria/mergeProps';

interface CalendarButtonProps extends DOMAttributes<HTMLButtonElement> {
  isDisabled?: boolean;
  isSelected?: boolean;
  hasCurrentIndicator?: boolean;
  tabIndex?: HTMLAttributes<HTMLButtonElement>['tabIndex'];
  isInGridCell?: boolean;
}

export const CalendarButton = React.forwardRef(function SapphireCalendarButton(
  {
    children,
    className = '',
    isSelected,
    isDisabled,
    isInGridCell,
    hasCurrentIndicator,
    ...otherProps
  }: CalendarButtonProps & { className?: string },
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const ElementType: 'span' | 'button' = isInGridCell ? 'span' : 'button';

  return (
    <FocusRing focusRingClass={styles['is-focus']}>
      <ElementType
        ref={forwardedRef}
        role="button"
        {...(ElementType === 'button' ? ({ type: 'button' } as const) : {})}
        {...mergeProps(otherProps, hoverProps)}
        className={clsx(
          className,
          styles['js-focus'],
          styles['js-hover'],
          styles['sapphire-calendar__button'],
          {
            [styles[`sapphire-calendar__button--selected`]]: isSelected,
            [styles['sapphire-calendar__button--current']]: hasCurrentIndicator,
            [styles[`is-hover`]]: isHovered,
            [styles[`is-disabled`]]: isDisabled,
          }
        )}
      >
        <span className={styles['sapphire-calendar__button-text']}>
          {children}
        </span>
      </ElementType>
    </FocusRing>
  );
});

export const CalendarDayButton = React.forwardRef(
  function SapphireCalendarDayButton(
    {
      isOutsideVisibleRange,
      rangeHighlight,
      ...props
    }: CalendarButtonProps & {
      rangeHighlight:
        | { highlighted: false }
        | { highlighted: true; start?: boolean | null; end?: boolean | null };
      isOutsideVisibleRange?: boolean;
    },
    forwardedRef: ForwardedRef<HTMLButtonElement>
  ) {
    return (
      <CalendarButton
        {...props}
        isInGridCell
        className={clsx(styles['sapphire-calendar__button--day'], {
          [styles['sapphire-calendar__button--range-selection']]:
            rangeHighlight.highlighted,
          [styles['sapphire-calendar__button--range-start']]:
            rangeHighlight.highlighted && rangeHighlight.start,
          [styles['sapphire-calendar__button--range-end']]:
            rangeHighlight.highlighted && rangeHighlight.end,
          [styles['sapphire-calendar__button--selection-start']]:
            rangeHighlight.highlighted &&
            rangeHighlight.start &&
            props.isSelected,
          [styles['sapphire-calendar__button--selection-end']]:
            rangeHighlight.highlighted &&
            rangeHighlight.end &&
            props.isSelected,
          [styles['sapphire-calendar__button--outside-month']]:
            isOutsideVisibleRange,
        })}
        ref={forwardedRef}
      />
    );
  }
);
